/**
 * Tab Leader Election
 *
 * Механизм выбора "главной" вкладки для управления WebSocket соединениями.
 * Использует localStorage для координации между вкладками и Broadcast Channel для синхронизации.
 *
 * Принцип работы:
 * 1. Каждая вкладка имеет уникальный tabId
 * 2. При старте вкладки выбирается один лидер
 * 3. Лидер остаётся лидером до закрытия вкладки
 * 4. Лидер регулярно обновляет heartbeat timestamp
 * 5. Если лидер не обновляет heartbeat (закрылся/завис), другие вкладки переизбирают нового лидера
 * 6. Переключение между вкладками НЕ вызывает смену лидерства
 */

const HEARTBEAT_INTERVAL = 5000; // Как часто лидер обновляет heartbeat (ms) - увеличено для снижения нагрузки
const LEADER_TIMEOUT = 30000; // Таймаут для признания лидера недоступным (ms) - увеличено для компенсации browser throttling
const ELECTION_DELAY = 200; // Базовая задержка перед попыткой стать лидером (ms)
const ELECTION_JITTER = 300; // Случайный джиттер для предотвращения одновременных выборов (ms)

type LeaderElectionCallback = () => void;

interface LeaderInfo {
  tabId: string;
  timestamp: number;
  isVisible?: boolean; // Признак активной (видимой) вкладки - только для диагностики
}

export class TabLeaderElection {
  private readonly namespace: string;
  private readonly tabId: string;
  private readonly storageKey: string;
  private readonly channelName: string;

  private isLeader = false;
  private isVisible = !document.hidden; // Отслеживание видимости вкладки
  private heartbeatTimer: number | null = null;
  private checkLeaderTimer: number | null = null;
  private channel: BroadcastChannel | null = null;
  private started = false;

  private onBecomeLeaderCallbacks: LeaderElectionCallback[] = [];
  private onLoseLeadershipCallbacks: LeaderElectionCallback[] = [];

  constructor(namespace: string) {
    this.namespace = namespace;
    // Используем crypto.randomUUID() для генерации уникального ID вкладки
    this.tabId = crypto.randomUUID();
    this.storageKey = `leader_election_${namespace}`;
    this.channelName = `leader_channel_${namespace}`;
  }

  /**
   * Запустить процесс выборов
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    // Инициализируем Broadcast Channel для координации
    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = (event) => {
        if (event.data?.type === 'heartbeat' && event.data?.tabId !== this.tabId) {
          if (this.isLeader) {
            // Конфликт двух лидеров: детерминистичный tiebreaker — побеждает больший tabId
            if (event.data.tabId > this.tabId) {
              this.becomeFollower();
            }
            // Если наш tabId больше — мы остаёмся лидером, конкурент уступит сам
          }
        }
      };
    } catch (error) {
      console.warn('[TabLeaderElection] Broadcast Channel не поддерживается', error);
    }

    // Пробуем стать лидером
    this.tryBecomeLeader();

    // Запускаем периодическую проверку состояния лидера
    this.scheduleLeaderCheck();

    // При закрытии вкладки - освобождаем лидерство
    window.addEventListener('beforeunload', () => {
      this.stop();
    });

    // При изменении видимости вкладки - обновляем heartbeat если мы лидер
    // НЕ пытаемся забрать лидерство - лидер остаётся лидером до закрытия вкладки
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;

      // Только обновляем heartbeat если мы лидер
      // Это нужно для диагностики, но НЕ влияет на выборы
      if (this.isLeader) {
        this.updateHeartbeat();
        this.broadcastHeartbeat();
      }
    });
  }

  /**
   * Остановить выборы и освободить лидерство
   */
  stop(): void {
    if (!this.started) return;
    this.started = false;

    this.clearHeartbeatTimer();
    this.clearLeaderCheckTimer();

    if (this.isLeader) {
      this.releaseLeadership();
    }

    if (this.channel) {
      try {
        this.channel.close();
      } catch (error) {
        console.warn('[TabLeaderElection] Ошибка при закрытии Broadcast Channel', error);
      }
      this.channel = null;
    }
  }

  /**
   * Проверить, является ли текущая вкладка лидером
   */
  isCurrentLeader(): boolean {
    return this.isLeader;
  }

  /**
   * Получить ID текущей вкладки
   */
  getTabId(): string {
    return this.tabId;
  }

  /**
   * Подписаться на событие "стал лидером"
   */
  onBecomeLeader(callback: LeaderElectionCallback): () => void {
    this.onBecomeLeaderCallbacks.push(callback);
    return () => {
      this.onBecomeLeaderCallbacks = this.onBecomeLeaderCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Подписаться на событие "потерял лидерство"
   */
  onLoseLeadership(callback: LeaderElectionCallback): () => void {
    this.onLoseLeadershipCallbacks.push(callback);
    return () => {
      this.onLoseLeadershipCallbacks = this.onLoseLeadershipCallbacks.filter(cb => cb !== callback);
    };
  }

  // Приватные методы

  private tryBecomeLeader(): void {
    // Случайная задержка для избежания race conditions между вкладками
    const delay = ELECTION_DELAY + Math.random() * ELECTION_JITTER;

    setTimeout(() => {
      const currentLeader = this.getCurrentLeader();

      // Становимся лидером ТОЛЬКО если лидера нет или он устарел
      if (!currentLeader || this.isLeaderExpired(currentLeader)) {
        this.becomeLeader();
      } else {
        // Есть активный лидер - становимся последователем
        // НЕ забираем лидерство у работающего лидера
        this.becomeFollower();
      }
    }, delay);
  }

  private becomeLeader(): void {
    if (this.isLeader) return;

    this.isLeader = true;
    this.updateHeartbeat();
    this.startHeartbeat();

    // Уведомляем другие вкладки
    this.broadcastHeartbeat();

    // Вызываем callbacks
    this.onBecomeLeaderCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[TabLeaderElection] Ошибка в onBecomeLeader callback', error);
      }
    });

    console.log(`[TabLeaderElection:${this.namespace}] Вкладка ${this.tabId} стала лидером`);
  }

  private becomeFollower(): void {
    const wasLeader = this.isLeader;

    if (wasLeader) {
      this.isLeader = false;
      this.clearHeartbeatTimer();

      // Вызываем callbacks
      this.onLoseLeadershipCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('[TabLeaderElection] Ошибка в onLoseLeadership callback', error);
        }
      });

      console.log(`[TabLeaderElection:${this.namespace}] Вкладка ${this.tabId} потеряла лидерство`);
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeatTimer();

    this.heartbeatTimer = window.setInterval(() => {
      if (this.isLeader) {
        this.updateHeartbeat();
        this.broadcastHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);
  }

  private updateHeartbeat(): void {
    const leaderInfo: LeaderInfo = {
      tabId: this.tabId,
      timestamp: Date.now(),
      isVisible: this.isVisible, // Сохраняем статус видимости для приоритизации
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(leaderInfo));
    } catch (error) {
      console.error('[TabLeaderElection] Ошибка при записи в localStorage', error);
    }
  }

  private broadcastHeartbeat(): void {
    if (!this.channel) return;

    try {
      this.channel.postMessage({
        type: 'heartbeat',
        tabId: this.tabId,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error('[TabLeaderElection] Ошибка при отправке heartbeat', error);
    }
  }

  private releaseLeadership(): void {
    if (!this.isLeader) return;

    try {
      const currentLeader = this.getCurrentLeader();
      if (currentLeader && currentLeader.tabId === this.tabId) {
        localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      console.error('[TabLeaderElection] Ошибка при освобождении лидерства', error);
    }

    this.becomeFollower();
  }

  private getCurrentLeader(): LeaderInfo | null {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return null;

      const leaderInfo = JSON.parse(data) as LeaderInfo;
      return leaderInfo;
    } catch (error) {
      console.error('[TabLeaderElection] Ошибка при чтении лидера из localStorage', error);
      return null;
    }
  }

  private isLeaderExpired(leaderInfo: LeaderInfo): boolean {
    const now = Date.now();
    return now - leaderInfo.timestamp > LEADER_TIMEOUT;
  }

  private checkAndClaimLeadership(): void {
    const currentLeader = this.getCurrentLeader();

    if (!currentLeader || this.isLeaderExpired(currentLeader)) {
      // Лидер устарел или отсутствует - можем стать лидером
      if (!this.isLeader) {
        this.tryBecomeLeader();
      }
    } else if (currentLeader.tabId === this.tabId) {
      // Мы - лидер, но возможно потеряли состояние
      if (!this.isLeader) {
        this.becomeLeader();
      }
    }
    // Если есть другой активный лидер - НЕ трогаем его
    // Лидер остаётся лидером до закрытия вкладки или timeout
  }

  private scheduleLeaderCheck(): void {
    this.clearLeaderCheckTimer();

    this.checkLeaderTimer = window.setInterval(() => {
      this.checkAndClaimLeadership();
    }, HEARTBEAT_INTERVAL);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearLeaderCheckTimer(): void {
    if (this.checkLeaderTimer !== null) {
      window.clearInterval(this.checkLeaderTimer);
      this.checkLeaderTimer = null;
    }
  }
}

/**
 * Создать экземпляр Leader Election для определённого namespace
 */
export function createLeaderElection(namespace: string): TabLeaderElection {
  return new TabLeaderElection(namespace);
}
